import { useMemo } from "react"

import { MapPin, Search, X } from "lucide-react"

import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import type { Location } from "@/types/collections"

interface LocationPickerProps {
  locations: Location[]
  selectedId: string
  /** Called with the chosen location's id, or "" to clear the choice. */
  onSelect: (locationId: string) => void
  searchTerm: string
  onSearchChange: (term: string) => void
}

export default function LocationPicker({
  locations,
  selectedId,
  onSelect,
  searchTerm,
  onSearchChange,
}: LocationPickerProps) {
  const filteredLocations = useMemo(() => {
    const active = locations.filter((l) => l.status === "active")
    if (!searchTerm.trim()) return active
    const search = searchTerm.toLowerCase()
    return active.filter(
      (l) =>
        l.site_name?.toLowerCase().includes(search) ||
        l.postcode?.toLowerCase().includes(search) ||
        (l.location_id && l.location_id.toLowerCase().includes(search)) ||
        l.address?.toLowerCase().includes(search)
    )
  }, [locations, searchTerm])

  const selectedLocation = useMemo(() => {
    if (!selectedId) return null
    return locations.find((l) => l.id === selectedId) || null
  }, [selectedId, locations])

  return (
    <div className="grid gap-2">
      <Label htmlFor="locationId">Location *</Label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-palette-jungleGreen400" />
        <Input
          id="locationId"
          placeholder="Search locations by name, postcode, or ID..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="h-9 border-palette-jungleGreen200 bg-white pl-9 text-sm"
        />
      </div>
      {selectedLocation && (
        <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-2">
          <MapPin size={14} className="text-amber-600" />
          <span className="text-sm font-medium">{selectedLocation.site_name}</span>
          <span className="text-xs text-palette-jungleGreen500">
            {selectedLocation.postcode}
          </span>
          <button
            type="button"
            onClick={() => onSelect("")}
            className="ml-auto text-palette-jungleGreen400 hover:text-palette-jungleGreen600"
          >
            <X size={14} />
          </button>
        </div>
      )}
      <div className="max-h-[180px] overflow-y-auto rounded-md border bg-white">
        {filteredLocations.length === 0 ? (
          <div className="p-4 text-center text-sm text-palette-jungleGreen500">
            {searchTerm ? "No locations match your search" : "No active locations available"}
          </div>
        ) : (
          <div className="divide-y">
            {filteredLocations.map((location) => {
              const isSelected = selectedId === location.id
              return (
                <div
                  key={location.id}
                  className={cn(
                    "flex cursor-pointer items-center gap-3 px-3 py-2 transition-colors hover:bg-palette-jungleGreen000",
                    isSelected && "bg-amber-50"
                  )}
                  onClick={() => onSelect(location.id)}
                >
                  <MapPin
                    size={14}
                    className={cn(
                      "text-palette-jungleGreen400",
                      isSelected && "text-amber-600"
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium">
                        {location.site_name}
                      </span>
                      {isSelected && (
                        <span className="rounded bg-amber-200 px-1.5 py-0.5 text-xs text-amber-800">
                          Selected
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-palette-jungleGreen500">
                      <span>{location.postcode}</span>
                      {location.location_id && (
                        <>
                          <span className="text-palette-jungleGreen300">•</span>
                          <span>{location.location_id}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
